import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, ButtonGroup } from "@mui/material";
import { useExperiences } from "../context/ExperiencesContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import GuideCard from "../components/GuideCard";
import { AppButton } from "../components/ui";
import "./GuidesListPage.scss";

const GuidesListPage = () => {
  const { guides, deleteGuide, fetchGuides } = useExperiences();
  const auth = useAuth();
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState("date"); // "date" | "name"

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const extractGuideId = (guide) => guide.sk.split("GUIDE#")[1];

  const safeDate = (g) => new Date(g?.dateCreated ?? g?.dateModified ?? g?.lastModified ?? 0).getTime();

  const sorters = {
    date: (a, b) => safeDate(b) - safeDate(a), // most recent first
    name: (a, b) => (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" }),
  };

  const { currentUserGuides, otherGuides } = useMemo(() => {
    const mine = (guides ?? []).filter((g) => g.creator === auth.userId);
    const others = (guides ?? []).filter((g) => g.creator !== auth.userId);
    const sorter = sorters[sortKey] ?? sorters.date;
    return {
      currentUserGuides: [...mine].sort(sorter),
      otherGuides: [...others].sort(sorter),
    };
  }, [guides, auth.userId, sortKey]);

  useEffect(() => {
    fetchGuides();
  }, []);

  const handleEdit = (guideId) => navigate(`/guides/${guideId}`);
  const handleClone = (guide) => navigate(`/guides/new?cloneFrom=${extractGuideId(guide)}`);
  const handleCreateNew = () => navigate("/guides/new");

  const handleDelete = async (guideId) => {
    try {
      await deleteGuide(guideId);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="guidesPage">
      <div className="guidesTopRow">
        <AppButton className="guidesCreateBtn" kind="primary" onClick={handleCreateNew}>
          Create New Guide
        </AppButton>

        <div className="guidesSortGroup">
          <ButtonGroup size="small" aria-label="sort guides">
            <AppButton
              kind={sortKey === "date" ? "primary" : "secondary"}
              onClick={() => setSortKey("date")}
            >
              Sort: Date Added
            </AppButton>
            <AppButton
              kind={sortKey === "name" ? "primary" : "secondary"}
              onClick={() => setSortKey("name")}
            >
              Sort: Name
            </AppButton>
          </ButtonGroup>
        </div>
      </div>

      <Typography className="guidesSectionTitle" variant="h5">
        My Guides
      </Typography>

      {currentUserGuides.length > 0 ? (
        <div className="guidesGrid">
          {currentUserGuides.map((guide) => (
            <GuideCard
              key={extractGuideId(guide)}
              guide={guide}
              isOwner
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <Typography className="guidesEmptyText">No guides available.</Typography>
      )}

      <Typography className="guidesSectionTitle" variant="h5" sx={{ mt: 4 }}>
        All Other Guides
      </Typography>

      {otherGuides.length > 0 ? (
        <div className="guidesGrid">
          {otherGuides.map((guide) => (
            <GuideCard
              key={extractGuideId(guide)}
              guide={guide}
              isOwner={false}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <Typography className="guidesEmptyText">No other guides available.</Typography>
      )}
    </div>
  );
};

export default GuidesListPage;
